import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useApolloClient } from '@apollo/client/react';
import { Avatar } from './Avatar';
import { ConfirmDialog } from './ConfirmDialog';
import { Dialog } from './Dialog';
import { REMOVE_AVATAR, UPLOAD_SERVICE_DOWN_HINT, UploadError, uploadAvatar, useFilesConfig, useUploadService } from '../lib/files';
import { ME, type Me } from '../lib/useMe';
import { refreshAvatars } from '../lib/useAvatars';
import { mutationErrorMessage } from '../lib/gqlError';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  cropFromView,
  initialView,
  panView,
  scaleOf,
  zoomView,
  type CropView,
} from '../lib/avatarCrop';
import '../styles/avatar.css';

/** What a profile photo may be, before the api is even asked (it re-checks type, size and magic bytes). */
const PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const POLL_MS = 1500;
const POLL_FOR_MS = 60_000;
const BOX = 280;
const KEY_STEP = 10;
const ZOOM_STEP = 0.1;

type Picked = { file: File; src: string; natW: number; natH: number };

/**
 * /settings/profile → your photo. Pick (png/jpg/webp ≤ 5 MB) → square crop → the ORIGINAL bytes go
 * up with the crop in original pixels → Transforge makes the avatar → `me.avatarUrl` flips, polled
 * here (network-only, every 1.5 s, at most 60 s) → `userAvatars` is refetched so every face on the
 * page updates. Hidden behind a hint when uploads (Transforge) are not configured;
 * disabled with "Upload service unavailable" when this browser cannot reach Transforge's /health.
 */
export function AvatarUpload({ me }: { me: Pick<Me, 'email' | 'avatarUrl'> }) {
  const client = useApolloClient();
  const cfg = useFilesConfig();
  const inputRef = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'processing' | 'removing'>('idle');
  const [note, setNote] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // the object URL lives exactly as long as the crop dialog's image
  useEffect(() => {
    if (!picked) return;
    return () => URL.revokeObjectURL(picked.src);
  }, [picked]);

  const enabled = !!cfg && cfg.uploadsEnabled && !!cfg.transforgeUrl;
  // SCIO health gate: Change photo stays disabled until this browser can reach Transforge
  const uploadService = useUploadService(enabled ? cfg.transforgeUrl : null);
  const allowed = PHOTO_TYPES.filter((t) => !cfg?.imageTypes?.length || cfg.imageTypes.includes(t));
  const maxBytes = Math.min(PHOTO_MAX_BYTES, cfg?.maxImageBytes || PHOTO_MAX_BYTES);
  const busy = phase !== 'idle';

  const onPick = (file: File | undefined) => {
    if (inputRef.current) inputRef.current.value = ''; // picking the same file again must re-fire
    if (!file) return;
    setToast(null);
    setNote(null);
    if (!allowed.includes(file.type)) {
      setToast(`"${file.name}" isn't a PNG, JPEG or WebP image.`);
      return;
    }
    if (file.size > maxBytes) {
      setToast(`"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB — a profile photo can be at most ${Math.round(maxBytes / 1024 / 1024)} MB.`);
      return;
    }
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        URL.revokeObjectURL(src);
        setToast(`"${file.name}" could not be read as an image.`);
        return;
      }
      setPicked({ file, src, natW: img.naturalWidth, natH: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(src);
      setToast(`"${file.name}" could not be read as an image.`);
    };
    img.src = src;
  };

  /** Poll `me.avatarUrl` until it is a URL different from `prev`; false when the window runs out. */
  const waitForAvatar = useCallback(
    async (prev: string | null) => {
      const deadline = Date.now() + POLL_FOR_MS;
      while (alive.current && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (!alive.current) return false;
        const r = await client.query<{ me: Me }>({ query: ME, fetchPolicy: 'network-only' }).catch(() => null);
        const url = r?.data?.me?.avatarUrl ?? null;
        if (url && url !== prev) return true;
      }
      return false;
    },
    [client],
  );

  const save = async (crop: { x: number; y: number; size: number }) => {
    if (!picked) return;
    const prev = me.avatarUrl ?? null;
    setPhase('uploading');
    try {
      const f = await uploadAvatar(client, {
        file: picked.file,
        crop,
        restrictions: { allowedFileTypes: allowed, maxFileSize: maxBytes },
      });
      if (f.status === 'failed') throw new UploadError('The photo could not be processed. Try another image.');
      if (!alive.current) return;
      setPicked(null);
      setPhase('processing');
      const done = await waitForAvatar(prev);
      if (!alive.current) return;
      await refreshAvatars(client);
      if (!done) setNote('Still processing — your photo will appear here once it is ready.');
    } catch (e) {
      if (!alive.current) return;
      setPicked(null); // the toast is not in the modal's top layer: close the dialog so it shows
      setToast(e instanceof UploadError ? e.message : (e as Error)?.message || 'Upload failed');
    } finally {
      if (alive.current) setPhase('idle');
    }
  };

  const remove = async () => {
    setPhase('removing');
    setToast(null);
    setNote(null);
    try {
      await client.mutate({ mutation: REMOVE_AVATAR });
      await Promise.all([
        client.query({ query: ME, fetchPolicy: 'network-only' }).catch(() => undefined),
        refreshAvatars(client),
      ]);
    } catch (e) {
      if (alive.current) setToast(mutationErrorMessage(e as Parameters<typeof mutationErrorMessage>[0]) || 'Could not remove the photo');
    } finally {
      if (alive.current) {
        setPhase('idle');
        setConfirmRemove(false);
      }
    }
  };

  return (
    <div className="avatar-upload" data-testid="avatar-upload">
      <Avatar email={me.email} className="avatar-lg" title={me.email} ariaHidden={false} />
      <div className="avatar-upload__actions">
        <div className="avatar-upload__buttons">
          {enabled && (
            <button
              type="button"
              className="btn"
              data-testid="avatar-change"
              data-upload-service={uploadService}
              disabled={busy || uploadService !== 'ok'}
              title={uploadService === 'down' ? UPLOAD_SERVICE_DOWN_HINT : undefined}
              onClick={() => inputRef.current?.click()}
            >
              {me.avatarUrl ? 'Change photo' : 'Upload photo'}
            </button>
          )}
          {me.avatarUrl && (
            <button
              type="button"
              className="btn"
              data-testid="avatar-remove"
              disabled={busy}
              onClick={() => setConfirmRemove(true)}
            >
              Remove photo
            </button>
          )}
          {enabled && (
            <input
              ref={inputRef}
              type="file"
              accept={allowed.join(',')}
              hidden
              data-testid="avatar-file"
              onChange={(e) => onPick(e.target.files?.[0])}
            />
          )}
        </div>
        {phase === 'uploading' && (
          <span className="avatar-upload__status" role="status">
            Uploading…
          </span>
        )}
        {phase === 'processing' && (
          <span className="avatar-upload__status" role="status" data-testid="avatar-processing">
            Processing…
          </span>
        )}
        {note && phase === 'idle' && <span className="avatar-upload__status">{note}</span>}
        {cfg && !enabled && (
          <span className="muted text-2xs" data-testid="avatar-disabled-hint">
            Profile photos need file processing (Transforge), which isn&apos;t configured here.
          </span>
        )}
        {enabled && uploadService === 'down' && phase === 'idle' && (
          <span className="muted text-2xs" data-testid="avatar-upload-unavailable">
            {UPLOAD_SERVICE_DOWN_HINT}
          </span>
        )}
        {enabled && uploadService !== 'down' && phase === 'idle' && !note && (
          <span className="muted text-2xs">PNG, JPEG or WebP, up to {Math.round(maxBytes / 1024 / 1024)} MB.</span>
        )}
      </div>

      <Dialog
        open={!!picked}
        onClose={() => setPicked(null)}
        title="Crop your photo"
        titleId="avatar-crop-title"
        size="sm"
        busy={phase === 'uploading'}
      >
        {picked && (
          <CropEditor
            picked={picked}
            busy={phase === 'uploading'}
            onCancel={() => setPicked(null)}
            onSave={(crop) => void save(crop)}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove your profile photo?"
        body="Everyone will see your initials instead."
        confirmLabel="Remove photo"
        cancelLabel="Keep photo"
        danger
        busy={phase === 'removing'}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmRemove(false)}
      />

      {toast &&
        createPortal(
          <div className="file-toast" role="alert" data-testid="file-toast">
            <span>{toast}</span>
            <button type="button" className="mini-btn mini-btn--icon" aria-label="Dismiss" onClick={() => setToast(null)}>
              ×
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** The square crop box: drag / arrows pan the image under it, slider / wheel / +- zoom. */
function CropEditor({
  picked,
  busy,
  onCancel,
  onSave,
}: {
  picked: Picked;
  busy: boolean;
  onCancel: () => void;
  onSave: (crop: { x: number; y: number; size: number }) => void;
}) {
  const [view, setView] = useState<CropView>(() => initialView(picked.natW, picked.natH, BOX));
  const boxRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);

  // wheel zoom must preventDefault (page scroll), which React's passive onWheel cannot
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * 0.0015);
      setView((v) => zoomView(v, v.zoom * factor, e.clientX - r.left, e.clientY - r.top));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (busy) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    drag.current = { id: d.id, x: e.clientX, y: e.clientY };
    setView((v) => panView(v, dx, dy));
  };
  const endDrag = () => {
    drag.current = null;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const k = e.key;
    let next: ((v: CropView) => CropView) | null = null;
    if (k === 'ArrowLeft') next = (v) => panView(v, KEY_STEP, 0);
    else if (k === 'ArrowRight') next = (v) => panView(v, -KEY_STEP, 0);
    else if (k === 'ArrowUp') next = (v) => panView(v, 0, KEY_STEP);
    else if (k === 'ArrowDown') next = (v) => panView(v, 0, -KEY_STEP);
    else if (k === '+' || k === '=') next = (v) => zoomView(v, v.zoom + ZOOM_STEP);
    else if (k === '-' || k === '_') next = (v) => zoomView(v, v.zoom - ZOOM_STEP);
    if (!next) return;
    e.preventDefault();
    setView(next);
  };

  const s = scaleOf(view);
  return (
    <div data-testid="avatar-crop-dialog">
      <div className="dialog-body avatar-crop">
        <div
          ref={boxRef}
          className="avatar-crop__box"
          style={{ width: BOX, height: BOX }}
          tabIndex={0}
          role="group"
          aria-label="Crop area. Drag or use the arrow keys to move the photo; plus and minus zoom."
          data-autofocus
          data-testid="avatar-crop-box"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
        >
          <img
            className="avatar-crop__img"
            src={picked.src}
            alt=""
            draggable={false}
            style={{
              width: picked.natW * s,
              height: picked.natH * s,
              transform: `translate(${view.offX}px, ${view.offY}px)`,
            }}
          />
          <span className="avatar-crop__mask" aria-hidden="true" />
        </div>
        <label className="avatar-crop__zoom">
          <span className="muted text-2xs">Zoom</span>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={view.zoom}
            data-testid="avatar-crop-zoom"
            aria-label="Zoom"
            disabled={busy}
            onChange={(e) => {
              const z = Number(e.target.value);
              setView((v) => zoomView(v, z));
            }}
          />
        </label>
        <p className="avatar-crop__hint">Drag or use the arrow keys to move · scroll, the slider or the + / − keys to zoom.</p>
      </div>
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-primary"
          data-testid="avatar-crop-save"
          disabled={busy}
          onClick={() => onSave(cropFromView(view))}
        >
          {busy ? 'Uploading…' : 'Save photo'}
        </button>
      </div>
    </div>
  );
}
